import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal, ModalHeader, ModalTitle } from "@/components/ui/modal";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase, supabaseSignUp } from "@/lib/supabase/client";
import type { UserTeamInsert } from "@/lib/supabase/client";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { logCodeAction } from "@/lib/codeAuditLog";
import { canManageRevelatio, REVELATIO_TEAM_ID } from "@/lib/auth";

interface Team {
  id: string;
  name: string;
}

interface ModalContentProps {
  isOpen?: boolean;
  onClose?: () => void;
  isRegisterPage?: boolean;
}

export function ModalContent({
  isOpen = true,
  onClose = () => {},
  isRegisterPage = false,
}: ModalContentProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [token, setToken] = useState("");
  const { toast } = useToast();
  const { user } = useAuth();

  const supabaseCurrentClient = isRegisterPage ? supabase : supabaseSignUp;

  useEffect(() => {
    const fetchTeams = async () => {
      const { data, error } = await supabase.from("teams").select("id, name");

      if (error) {
        toast({
          variant: "destructive",
          title: "Erro ao buscar equipes",
          description: error.message,
        });
        return;
      }

      const includeRevelatio =
        !isRegisterPage && user?.id
          ? await canManageRevelatio(user.id)
          : false;
      const filteredTeams = (data || []).filter(
        (team) => team.id !== REVELATIO_TEAM_ID || includeRevelatio
      );
      setTeams(filteredTeams);
    };

    if (isOpen || isRegisterPage) {
      fetchTeams();
    }
  }, [isOpen, isRegisterPage, user?.id, toast]);

  useEffect(() => {
    if (isAdmin) {
      setSelectedTeams(teams.map((team) => team.id));
    }
  }, [isAdmin, teams]);

  const handleSubmit = async () => {
    if (!name || !email || !password || selectedTeams.length === 0) {
      toast({
        variant: "destructive",
        title: "Erro de validação",
        description:
          "Preencha todos os campos obrigatórios e selecione pelo menos uma equipe",
      });
      return;
    }

    if (isRegisterPage && token !== import.meta.env.VITE_REGISTRATION_TOKEN) {
      toast({
        variant: "destructive",
        title: "Token inválido",
        description: "O token fornecido é inválido",
      });
      return;
    }

    const payload = {
      email,
        password,
        options: {
          data: {
            name,
          },
        },
    }

    const actor = user;

    try {
      const {
        data: { user },
        error: signUpError,
      } = await supabaseCurrentClient.auth.signUp(payload);

      if (signUpError) throw signUpError;
      if (!user) throw new Error("User creation failed");

      const teamAssignments: UserTeamInsert[] = selectedTeams.map((teamId) => ({
        user_id: user.id,
        team_id: teamId,
      }));

      await supabase.from("user_teams").insert(teamAssignments);

      // ponytail: só loga quando um admin logado cria (na página de registro não há sessão)
      if (actor) {
        logCodeAction({
          userId: actor.id,
          userEmail: actor.email ?? "",
          action: "member_created",
          codeType: "member",
          referenceId: user.id,
          codeLabel: email,
        });
      }

      toast({
        title: "Sucesso",
        description: "Usuário criado com sucesso",
      });
      onClose();
      resetForm();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message,
      });
    }
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setPassword("");
    setIsAdmin(false);
    setSelectedTeams([]);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={
        isRegisterPage ? "px-4 py-2 max-sm:w-auto rounded-sm" : "px-4 py-2"
      }
    >
      {isRegisterPage ? (
        <ModalHeader className=" max-sm:h-5">
          <div className="text-center mt-2 max-sm:mt-0">
            <div className="flex justify-center">
              <img src="/logo-auth-dark.png" alt="Serur Auth" className="h-9 w-auto" />
            </div>
          </div>
          <ModalTitle className="pt-2 flex justify-center text-center text-md text-muted-foreground max-sm:pt-0 mt-0 text-sm">
            Faça Seu Cadastro
          </ModalTitle>
        </ModalHeader>
      ) : (
        <ModalHeader>
          <ModalTitle className="pt-3">Adicionar Novo Usuário</ModalTitle>
        </ModalHeader>
      )}

      <div className="mt-1 space-y-4 max-[480px]:space-y-3">
        <div className="space-y-2 max-[480px]:space-y-0">
          <Label htmlFor="name" className="text-foreground">
            Nome
          </Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-secondary border-border text-foreground"
            placeholder={
              isRegisterPage ? "Digite seu nome" : "Digite o nome do usuário"
            }
          />
        </div>

        <div className="space-y-2 max-[480px]:space-y-0">
          <Label htmlFor="email" className="text-foreground">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-secondary border-border text-foreground"
            placeholder={
              isRegisterPage ? "Digite seu email" : "Digite o email do usuário"
            }
          />
        </div>

        <div className="space-y-2 max-[480px]:space-y-0">
          <Label htmlFor="password" className="text-foreground">
            Senha
          </Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-secondary border-border text-foreground"
            placeholder={
              isRegisterPage ? "Digite sua senha" : "Digite a senha do usuário"
            }
          />
        </div>

        {isRegisterPage && (
          <div className="space-y-2 max-[480px]:space-y-0">
            <Label htmlFor="token" className="text-foreground">
              Token
            </Label>
            <Input
              id="token"
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="bg-secondary border-border text-foreground"
              placeholder="Digite o token para registro"
            />
          </div>
        )}

        {!isRegisterPage && (
          <div className="space-y-2">
            <Label className="text-foreground">Nível do Usuário</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                className={`
                border-border 
                ${
                  !isAdmin
                    ? "bg-primary text-primary-foreground"
                    : "bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent"
                }
              `}
                onClick={() => setIsAdmin(false)}
              >
                Usuário
              </Button>
              <Button
                variant="outline"
                className={`
                border-border 
                ${
                  isAdmin
                    ? "bg-primary text-primary-foreground"
                    : "bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent"
                }
              `}
                onClick={() => setIsAdmin(true)}
              >
                Admin
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-foreground">Selecionar Times</Label>
          <div className="grid grid-cols-2 gap-2">
            {teams.map((team) => (
              <Button
                key={team.id}
                variant="outline"
                disabled={isAdmin}
                className={`
                  border-border 
                  ${
                    selectedTeams.includes(team.id)
                      ? "bg-primary text-primary-foreground"
                      : "bg-transparent text-muted-foreground hover:text-foreground hover:bg-accent"
                  }
                  ${isAdmin && "opacity-50 cursor-not-allowed"}
                  max-[480px]:whitespace-normal
                  max-[480px]:text-xs
                `}
                onClick={() => {
                  if (!isAdmin) {
                    setSelectedTeams((prev: string[]) =>
                      prev.includes(team.id)
                        ? prev.filter((id: string) => id !== team.id)
                        : [...prev, team.id]
                    );
                  }
                }}
              >
                {team.name}
              </Button>
            ))}
          </div>
          {isAdmin && (
            <p className="text-sm text-muted-foreground mt-2">
              Administradores têm acesso automático a todos os times
            </p>
          )}
        </div>

        <Button
          className="w-full mt-6 bg-primary text-primary-foreground hover:bg-primary/90 mb-2"
          onClick={handleSubmit}
        >
          {isRegisterPage ? "Cadastrar" : "Adicionar Usuário"}
        </Button>
      </div>
      {isRegisterPage ? (
        <div className="flex justify-center  text-muted-foreground ">
          <p>
            Já tem conta ?{" "}
            <Link
              to="/"
              className="hover:text-foreground hover:bg-accent cursor-pointer"
            >
              Login
            </Link>
          </p>
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
